import { type WhatsAppContactId } from '@/config/whatsappContacts';
import { buildWhatsAppUrl } from '@/utils/whatsapp';

import { usePageWhatsAppMessage } from './usePageWhatsAppMessage';
import { useWhatsAppContact } from './useWhatsAppContact';

export type UseWhatsAppLinkOptions = {
  /** What the message says. Defaults to the route's own message. */
  message?: string | undefined;
  /** A seat other than the page's. See `useWhatsAppContact`. */
  contactId?: WhatsAppContactId | null | undefined;
};

/**
 * The finished `wa.me` link for this page: the route's number and the route's
 * message, already joined.
 *
 * It exists because three controls draw their own WhatsApp affordance instead
 * of using `WhatsAppButton` — the desktop floating action, and the icon buttons
 * on the vehicle and accessory cards — and each of them had its own copy of
 * `company.whatsappE164 ? buildWhatsAppUrl(…) : null`. Three copies of the
 * number lookup is three places that would have missed the route map.
 *
 * Null means no link: either WhatsApp is off site-wide, or the number in the
 * config is not dialable. Callers render nothing rather than a dead button.
 */
export function useWhatsAppLink({ message, contactId }: UseWhatsAppLinkOptions = {}): string | null {
  const pageMessage = usePageWhatsAppMessage();
  const contact = useWhatsAppContact(contactId);
  if (!contact) return null;
  return buildWhatsAppUrl(contact.e164, message ?? pageMessage);
}
